import { ArrowUpRight } from "lucide-react"

// Helper function to create FAQ link buttons
function createFAQLink(faqId: string, text: string, className?: string) {
  return (
    <button
      type="button"
      onClick={() => {
        const el = document.getElementById(`faq-${faqId}`)
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" })
        } else {
          document.getElementById("faqs")?.scrollIntoView({ behavior: "smooth" })
        }
        window.dispatchEvent(new CustomEvent("openFAQ", { detail: { faqId } }))
      }}
      className={className || "text-[#22FF65] underline decoration-[#22FF65] font-medium cursor-pointer"}
    >
      {text}
    </button>
  )
}

// Helper function to create external links
function createExternalLink(href: string, text: string, className?: string) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={
        className ||
        "inline-flex items-center font-medium text-[#30DFFF] hover:text-[#30DFFF] active:opacity-80 transition-all"
      }
    >
      {text}
      <ArrowUpRight className="w-[1.05rem] h-[1.05rem] translate-y-[-0.075rem] ml-[-0.05rem] opacity-80 group-hover:opacity-100 transition-all" />
    </a>
  )
}

// FAQ answers mapped by ID
export const faqAnswers: Record<string, React.JSX.Element> = {
  "0-0": (
    <>
      <p>
        <span className="text-white font-medium">
          Bracket is the fan-powered social betting network where crowds Bet & Trade Live™ on sports' biggest moments
          with @bracky, the world's most advanced AI Sports Agent
        </span>
      </p>

      <p>
        Brackets are {createFAQLink("0-3", "Sports Prediction Markets")} where fans actively buy and sell outcomes using{" "}
        {createFAQLink("0-2", "$BRACKY")}, the token that powers the network. As drama unfolds, fans drive the odds as
        they adjust their positions, or hold to win.
      </p>

      <p>
        <span className="text-white font-medium">AI Sports Intelligence™:</span> Just mention{" "}
        {createFAQLink("0-1", "@bracky")}, the world's most advanced <span className="text-white">AI Sports Agent</span>
        , directly in your social feed to get strategic insights for placing smart bets. Bracky is your personal betting
        companion who learns your style and gets sharper with every game.
      </p>

      <p>
        <span className="text-white font-medium">Live The Game™:</span> Bracket transforms you from watching the action
        to playing in it, immersing you in Sports' Biggest Moments as they happen alongside a community of fans living
        the game together.{" "}
      </p>
    </>
  ),

  "0-1": (
    <>
      <p>
        <span className="text-white font-medium">
          @bracky is the World's #1 AI Sports Agent. Mention him to book your sports bets in any* social feed, group
          chat, or terminal
        </span>
      </p>

      <p>
        <span className="block font-normal mt-1">*Presently on Farcaster & The Base App—Others coming soon…</span>
      </p>

      <p>
        <span className="text-white font-medium">Bracky meets you wherever the sports conversation is happening</span>
        <span className=""> and lets you instantly join in, learn and predict.</span>
      </p>

      <p>
        <span className="text-white font-medium">To get started, send a message to </span>
        {createExternalLink("https://x.com/BrackyHQ", "@BrackyHQ")} on X (Twitter) or{" "}
        {createExternalLink("https://farcaster.xyz/bracky/casts-and-replies", "@bracky")} on Farcaster or The Base App
        to bet sports' biggest moments right in the timeline.{" "}
      </p>

      <p>
        <span className="text-white font-medium">Bracky runs markets behind the scenes</span>—monitoring events, booking
        bets, answering questions about your positions, in-game action, market activity and more.
      </p>

      <p>
        To execute your bets, Bracky uses {createFAQLink("0-2", "$BRACKY")} tokens from your{" "}
        <span className="text-white font-medium">Bracket Wallet</span> which you fully control—deposit or withdraw
        anytime.
      </p>

      <p>
        Learn more about Bracky's superpowers in the{" "}
        <button
          type="button"
          onClick={() => {
            const el = document.getElementById("bracky")
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" })
            }
          }}
          className="text-[#22FF65] underline decoration-[#22FF65] font-medium cursor-pointer"
        >
          @bracky section
        </button>{" "}
        above.
      </p>
    </>
  ),

  "0-2": (
    <>
      <p className="font-medium text-white">
        <a
          href="https://app.uniswap.org/explore/tokens/base/0x06f71fb90F84b35302d132322A3C90E4477333b0"
          target="_blank"
          rel="noopener noreferrer"
          className="group inline-flex items-center text-[#30DFFF] font-medium hover:text-[#30DFFF] active:opacity-50 transition-all"
        >
          $BRACKY
          <ArrowUpRight className="w-[1.18rem] h-[1.18rem] translate-y-[-0.075rem] ml-[-0.1rem] mr-[-0.1rem] opacity-80 group-hover:opacity-100  transition-all" />
        </a>
        <span className="ml-1 font-white">(on Base)</span>
        <span className="ml-1">is the official token of Bracket</span>
      </p>

      <p>
        All of your sports bets on Bracket are made using the <span className="text-white">$BRACKY</span> token, and all
        of your <span className="text-white">payouts</span> are received back in the same token as well.
      </p>

      <p>
        <span className="text-white/80">If you ever want to convert your</span>{" "}
        <span className="text-white">$BRACKY</span>{" "}
        <span className="text-white/80">
          to another currency (like US Dollars), you can simply use our Coinbase flow to sell the
        </span>{" "}
        <span className="text-white">$BRACKY</span>{" "}
        <span className="text-white/80">at market price, and transfer it to your bank account.</span>
      </p>

      <p>
        Just like <span className="text-white">Bitcoin</span> ($BTC) or <span className="text-white">Ethereum</span> ($
        ETH), <span className="text-white">$BRACKY</span>'s market price goes up when there is increasing demand for
        acquiring the token (and vice versa).
      </p>

      <p>
        <span className="text-white font-medium">
          Fans who receive an <span className="text-white font-medium">invite</span>
          <span className="text-white/80 font-normal">
            {" "}
            start with free <span className="text-white">$BRACKY</span> to place their first bets.
          </span>
        </span>
      </p>

      <p>To stack more, you can:</p>

      <div className="pl-3 space-y-3 text-white">
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-0 shrink-0">▰</span>
          <div>
            <span className="text-white/80">
              Buy with your <span className="text-white font-medium">Debit Card</span>
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-[-.1rem] shrink-0">▰</span>
          <div>
            <span className="text-white/80">Swap for other crypto tokens</span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-[-.15rem] shrink-0">▰</span>
          <div>
            <span className="text-white/80">
              Purchase on a trusted marketplace, <br />
              such as {createExternalLink("https://coinbase.com", "Coinbase")}
            </span>
          </div>
        </div>
      </div>

      <div className="flex flex-col items-center mt-4 space-y-4">
        <a
          href="https://app.uniswap.org/explore/tokens/base/0x06f71fb90F84b35302d132322A3C90E4477333b0"
          target="_blank"
          rel="noopener noreferrer"
          className="relative inline-flex items-center justify-center mt-1 w-full sm:w-2/3 bg-[#001CFF] text-white font-medium px-10 sm:px-12 py-3 rounded-full transition-all whitespace-nowrap hover:brightness-110 active:brightness-90"
        >
          <span className="inline-block w-[.88rem] h-[.88rem] mr-2 bg-white rounded-[1px] shrink-0" />
          Buy $BRACKY
        </a>
        <div className="flex items-center justify-center gap-1 text-xs text-white/50 select-all">
          <span className="sm:hidden">CA: 0x06f7...33b0</span>
          <span className="hidden sm:inline">CA: 0x06f71fb90F84b35302d132322A3C90E4477333b0</span>
        </div>
      </div>
    </>
  ),

  "0-3": (
    <>
      <p>
        <span className="text-white font-medium">
          Bracket is a live markets platform for the world's biggest sports events
        </span>
      </p>

      <p>
        Buy and sell real-world outcomes—like who will win a game or tournament—using {createFAQLink("0-2", "$BRACKY")}{" "}
        tokens. Odds move between <span className="text-white font-medium">0%</span>{" "}
        <span className="text-white">and</span> <span className="text-white font-medium">100%</span> based on how much
        the crowd believes in each outcome.{" "}
      </p>

      <p>
        <span className="text-white font-medium"> How to Bet & Trade Live™:</span>{" "}
      </p>

      <div className="pl-3 space-y-3 text-white">
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-0 shrink-0">▰</span>
          <div>
            <span className="text-[#22FF65] mr-0">▲ </span>
            <span className="text-[#22FF65] mr-[.25rem] font-medium">Buy</span>
            {""}
            <span className="text-white/80">
              with <span className="text-white opacity-100">$BRACKY</span> when a team or outcome is undervalued
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-[-.1rem] shrink-0">▰</span>
          <div>
            <span className="text-[#FF2D30] mr-1">▼</span>
            <span className="text-[#FF2D30] mr-[.25rem] font-medium">Sell</span>
            {""}
            <span className="text-white/80">
              for <span className="text-white opacity-100">$BRACKY</span> when you feel momentum shift…
            </span>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/90 mt-[-.15rem] shrink-0">▰</span>
          <div>
            <span className="inline-block mr-0 translate-y-[-.1rem]">💪</span>
            <span className="text-[#ffffff] font-medium"> Hold to Win</span>{" "}
            <span className="text-white/80">if you truly believe!</span>
          </div>
        </div>
      </div>

      <p>
        Betting ends when events finalize. After results are verified, your winning positions receive full payouts in{" "}
        <span className="text-white">$BRACKY</span>.
      </p>

      <p>
        <span className="text-white">Getting in is easy</span>—just mention {createFAQLink("0-1", "@bracky")} in your
        social feed to place bets instantly, or bet directly using the{" "}
        <span className="text-white font-medium">Bracket Trading Terminal</span> (coming soon). It's fast, simple, and
        real-time. All you need to start is your <span className="text-white font-medium">free $BRACKY balance</span>.
      </p>
    </>
  ),

  "0-4": (
    <>
      <p>
        <span className="text-white font-medium">To start betting on Bracket</span>, just mention{" "}
        {createExternalLink("https://x.com/BrackyHQ/with_replies", "@BrackyHQ")} on X or{" "}
        {createExternalLink("https://farcaster.xyz/bracky/casts-and-replies", "@bracky")} in your Base or Farcaster
        feed.
      </p>

      <p>
        Once you've made your first bet, head to {createExternalLink("https://bracky.app", "Bracky.app")} to claim your
        wallet, view your betting history, deposit more <span className="text-white">$BRACKY</span> or cash out, and
        start trading sports' biggest moments live in the app.
      </p>
    </>
  ),

  "1-0": (
    <>
      <p>
        You can <span className="text-white font-medium">Trade Live™</span> in two ways:
      </p>

      <p>
        <span className="text-white font-medium">In Feed Mode:</span> Just mention{" "}
        {createExternalLink("https://x.com/BrackyHQ", "@BrackyHQ")} on X (Twitter) or{" "}
        {createExternalLink("https://farcaster.xyz/bracky/casts-and-replies", "@bracky")} on Farcaster and The Base App
        with your trade.
      </p>

      <p className="ml-4 text-white/80">
        For example: "@bracky buy me 100&nbsp;Chiefs shares" or "@bracky spend 500&nbsp;$BRACKY on the Ravens."
      </p>

      <p className="ml-4 text-white/80">
        Bracky understands both share amounts and $BRACKY amounts and executes trades using your{" "}
        {createFAQLink("4-0", "Bracket Wallet")} balance.
      </p>

      <p>
        <span className="text-white font-medium">In App Mode:</span> Open a live bracket → tap the outcome you want →
        select how many shares → confirm your trade. Prices update in real-time as the action unfolds.
      </p>
      <p>
        <span className="text-white font-medium">How it works:</span>
      </p>

      <div className="space-y-1 text-white/80">
        <div className="flex items-start gap-2">
          <span className="text-white/50 mt-[0.1rem] shrink-0">▰</span>
          <span>
            Share prices move between 0–100 {createFAQLink("2-0", "$BRACKY")} reflecting the % chance of the outcome
            based on community trading activity
          </span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/50 mt-[0.1rem] shrink-0">▰</span>
          <span>You can buy, sell, or hold until the final buzzer</span>
        </div>
        <div className="flex items-start gap-2">
          <span className="text-white/50 mt-[0.1rem] shrink-0">▰</span>
          <span>Winning shares always pay out +100 $BRACKY each!</span>
        </div>
      </div>

      <p>
        <span className="text-white font-medium">Trading tip:</span> Select outcomes while using{" "}
        <span className="text-white font-medium">In Feed Mode</span> to instantly generate trades to send to @bracky in
        your feed. Learn more about trading with Bracky {createFAQLink("1-4", "here")}.
      </p>
    </>
  ),
}
